import { z } from "zod";

/**
 * Which snapshot(s) a rebuild targets. Only the `standby-pair` strategy has more
 * than one target; under `reconciled` the only accepted value is `"full"` (or
 * none), and the admin endpoint refuses the other two with HTTP 409.
 */
export const CueRebuildModeSchema = z.enum(["full", "primary", "standby"]);

export type CueRebuildMode = z.infer<typeof CueRebuildModeSchema>;

/**
 * The fields both strategies report.
 *
 * `isRebuilding` is in-pod in-memory — a bake (or a reconcile kick) in flight ON
 * THE POD THAT ANSWERED. `lastRebuildError` / `lastRebuildErrorAt` are
 * Redis-backed and cross-pod accurate.
 *
 * `skillsUrl` is the resolved `CUE_SKILLS_URL` — the S3 object the admin skills
 * upload writes and every bake reads its bytes from; surfaced so ops can see it.
 * NULLABLE because `CUE_SKILLS_URL` is optional: `null` is "no skill source
 * configured", never a blank the panel would have to guess about.
 *
 * `lastRebuildErrorAt` is an ISO string, not a `Date`: it is read straight out of
 * Redis, so `string | null` is the only shape the backend can produce.
 */
const rebuildStatusCommonShape = {
  isRebuilding: z.boolean(),
  lastRebuildError: z.string().nullable(),
  lastRebuildErrorAt: z.string().nullable(),
  skillsUrl: z.string().nullable()
};

/**
 * `DAYTONA_SNAPSHOT_RECONCILE_ENABLED` off: a rebuild deletes and recreates
 * `CUE_SNAPSHOT_NAME` and its `-standby` under their fixed names, one at a time,
 * so the other serves during the window. Both availabilities are live Daytona
 * state at query time.
 */
export const CueStandbyPairRebuildStatusSchema = z.object({
  strategy: z.literal("standby-pair"),
  ...rebuildStatusCommonShape,
  primarySnapshotName: z.string(),
  standbySnapshotName: z.string(),
  primaryAvailable: z.boolean(),
  standbyAvailable: z.boolean()
});

/**
 * `DAYTONA_SNAPSHOT_RECONCILE_ENABLED` on: the snapshot is named by its content
 * (`<CUE_SNAPSHOT_NAME>-<env>-<fingerprint>`), a rebuild bakes the new name beside
 * the one serving, and nothing is deleted or swapped until the new one is
 * `active`. There is no standby to report.
 *
 * `servingSnapshotName` is the snapshot a new Cue sandbox would boot from right
 * now — a reconciled name, else `CUE_SNAPSHOT_NAME`, else its standby while it
 * still exists — and `null` when none is active (or availability could not be
 * established). `servingAvailable` is exactly `servingSnapshotName !== null`.
 */
export const CueReconciledRebuildStatusSchema = z.object({
  strategy: z.literal("reconciled"),
  ...rebuildStatusCommonShape,
  servingSnapshotName: z.string().nullable(),
  servingAvailable: z.boolean()
});

/**
 * Rebuild state surfaced by `GET /api/admin/cue/snapshot/status`.
 * Shared between the backend's return type and the admin status panel, so a
 * field change on either side is a typecheck break on the other rather than a
 * silent UI gap. Discriminated on `strategy`, because the two strategies have
 * different snapshots to report and a field that exists for one is false for
 * the other.
 */
export const CueRebuildStatusSchema = z.discriminatedUnion("strategy", [
  CueStandbyPairRebuildStatusSchema,
  CueReconciledRebuildStatusSchema
]);

export type CueRebuildStatus = z.infer<typeof CueRebuildStatusSchema>;
export type CueStandbyPairRebuildStatus = z.infer<typeof CueStandbyPairRebuildStatusSchema>;
export type CueReconciledRebuildStatus = z.infer<typeof CueReconciledRebuildStatusSchema>;
